import { forwardRef, useState } from 'react';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { CharacterCount } from '../../common-components/character-count/character-count.component';
import { InputMessage } from '../../common-components/input-message/input-message.component';
import { Label } from '../../common-components/label/label.component';

export type TextAreaProps = {
	ariaLabel?: string;
	className?: string;
	disabled?: boolean;
	invalid?: boolean;
	invalidMessage?: string;
	name?: string;
	testId?: string;
	onBlur?: (data: React.FocusEvent<HTMLTextAreaElement>) => void;
	onChange?: (data: React.ChangeEvent<HTMLTextAreaElement>) => void;
	onFocus?: (data: React.FocusEvent<HTMLTextAreaElement>) => void;
	onKeyDown?: (data: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	placeholder?: string;
	required?: boolean;
	value?: string | number;
	rows?: number;
	automationHook?: string;
	analyticsHook?: string;
	hasLabel?: boolean;
	disableWidthResize?: boolean;
	description?: string;
	readOnly?: boolean;
	maxLength?: number;
	includeCounter?: boolean;
	defaultValue?: string;
	size?: 'default' | 'large';
};

export type TextAreaWithLabelProps = TextAreaProps & {
	label: string;
	labelClassName?: string;
	id?: string;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
	(
		{
			ariaLabel,
			className = '',
			disabled,
			invalid,
			invalidMessage,
			name,
			testId = 'textArea',
			onBlur,
			onChange,
			onFocus,
			onKeyDown,
			placeholder,
			required,
			value,
			rows,
			automationHook,
			analyticsHook,
			hasLabel = false,
			disableWidthResize = false,
			description,
			readOnly,
			maxLength,
			includeCounter = false,
			size = 'default'
		},
		ref
	) => {
		const [currentCount, setCurrentCount] = useState(value?.toString().length || 0);
		const sizePadding =
			size === 'large'
				? 'theme-luxury:pt-4! theme-luxury:pb-4! theme-luxury:pl-6! theme-luxury:pr-6!'
				: 'theme-luxury:pt-2! theme-luxury:pb-2! theme-luxury:pl-4! theme-luxury:pr-4!';

		const resizeClasses = disableWidthResize
			? 'theme-luxury:resize-none! theme-luxury:hover:resize-y!'
			: 'theme-luxury:resize-none! theme-luxury:hover:resize!';

		const containerClasses = `theme-luxury:input! theme-luxury:radius-2xl! theme-luxury:border! theme-luxury:disabled:border-disabled! theme-luxury:disabled:bg-disabled! ${sizePadding} ${
			invalid ? 'invalid-error theme-luxury:shadow-error!' : ''
		} ${resizeClasses} input-reset br2 w-100 flex flex-column overflow-hidden`;

		return (
			<div className="flex flex-column w-100">
				<div className={containerClasses} style={{ resize: disableWidthResize ? 'vertical' : 'both' }}>
					<textarea
						className={`${className} input-reset outline-0 br2 ba f5 ph3 py-3 w-100 h-100 resize-none ${
							invalid ? 'b--theme-error' : 'b--theme-grey-light'
						} theme-luxury:p-0! theme-luxury:font-light! theme-luxury:text-primary-500! theme-luxury:border-none! theme-luxury:shadow-none! theme-luxury:bg-transparent! theme-luxury:placeholder:text-placeholder! theme-luxury:placeholder:leading-6! theme-luxury:disabled:placeholder:text-disabled!`}
						data-testid={testId}
						id={name}
						aria-label={!hasLabel && ariaLabel ? ariaLabel : undefined}
						aria-invalid={invalid || undefined}
						name={name}
						onBlur={onBlur}
						onInput={(e) => setCurrentCount(e.currentTarget.value.length)}
						onChange={onChange}
						onFocus={onFocus}
						onKeyDown={onKeyDown}
						placeholder={placeholder}
						required={required}
						value={value}
						rows={rows}
						ref={ref}
						data-automation={generateDataSelector('textarea', automationHook)}
						data-analytics={generateDataSelector('textarea', analyticsHook)}
						readOnly={readOnly}
						disabled={disabled}
						maxLength={maxLength}
					/>
				</div>
				{(includeCounter || invalid || description) && (
					<div className="flex justify-between w-auto">
						<InputMessage invalidMessage={invalidMessage} invalid={invalid} description={description} />
						{includeCounter && (
							<CharacterCount testId={testId} invalid={invalid} currentCount={currentCount} maxLength={maxLength} />
						)}
					</div>
				)}
			</div>
		);
	}
);

export const TextAreaWithLabel = forwardRef<HTMLTextAreaElement, TextAreaWithLabelProps>(
	({ label, id, required, labelClassName, ...props }, ref) => (
		<Label label={label} required={required} id={id} className={labelClassName}>
			<TextArea {...props} ref={ref} hasLabel={true} />
		</Label>
	)
);
